import React, { useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, CopySimple } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PrimaryButton, GhostButton } from "@/components/admin/parts";
import { adminApi, getErrorMessage } from "@/lib/api";

function TargetRow({ s, gen, checked, onToggle }) {
  const has = s.generations_entered.includes(gen);
  return (
    <label className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/[0.03] cursor-pointer" data-testid="ms-apply-target">
      <input type="checkbox" checked={checked} onChange={onToggle} className="accent-[#73bf44] w-4 h-4" />
      <span className="text-sm text-neutral-200 flex-1 truncate">{s.name}</span>
      <span className="text-[0.65rem] text-neutral-500 shrink-0">{s.vans} vans</span>
      {has && <span className="text-[0.6rem] font-semibold uppercase tracking-wider text-amber-300 shrink-0">Gen {gen} entered · replaced</span>}
    </label>
  );
}

export function ApplyDialog({ open, onClose, spec, gen, allSpecs, onApplied }) {
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const others = allSpecs.filter((s) => s.id !== spec.id && s.name.toLowerCase().includes(q.trim().toLowerCase()));
  const toggle = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const label = gen ? `Generation ${gen}` : "current";

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.post(`/admin/model-specs/${spec.id}/generations/${gen}/apply`, { target_spec_ids: [...picked] });
      toast.success(`Standard features copied to ${data.applied} model${data.applied === 1 ? "" : "s"}`);
      setPicked(new Set());
      onApplied();
      onClose();
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F0F0F] border-neutral-800 text-neutral-200 max-w-lg" data-testid="ms-apply-dialog">
        <DialogHeader><DialogTitle className="font-heading text-white">Apply {label} standard features</DialogTitle></DialogHeader>
        <p className="text-sm text-neutral-400 -mt-1">
          Copies the saved {label} standard features of <span className="text-white">{spec.name}</span> to the models you tick. Measurements and weights aren't changed. Each model keeps its own copy you can edit later.
        </p>
        <div className="flex items-center gap-2 bg-black/40 border border-neutral-800 rounded-lg px-3 py-2 focus-within:border-[#73bf44]">
          <MagnifyingGlass size={14} className="text-neutral-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search models…" data-testid="ms-apply-search" className="bg-transparent outline-none text-sm text-white w-full placeholder:text-neutral-600" />
        </div>
        <div className="max-h-72 overflow-y-auto border border-neutral-800 rounded-xl p-1" data-testid="ms-apply-list">
          {others.length === 0 ? <p className="text-sm text-neutral-600 p-3">No models match.</p> : others.map((s) => (
            <TargetRow key={s.id} s={s} gen={gen} checked={picked.has(s.id)} onToggle={() => toggle(s.id)} />
          ))}
        </div>
        <div className="flex items-center justify-between gap-2 pt-1">
          <span className="text-xs text-neutral-500" data-testid="ms-apply-count">{picked.size} selected</span>
          <div className="flex gap-2">
            <GhostButton onClick={onClose}>Cancel</GhostButton>
            <PrimaryButton onClick={apply} disabled={!picked.size || busy} data-testid="ms-apply-submit"><CopySimple size={16} /> {busy ? "Applying…" : `Apply to ${picked.size || ""} model${picked.size === 1 ? "" : "s"}`.replace("  ", " ")}</PrimaryButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
